<template>
  <div class="estate-form">
    <div class="estate-header" @click="expanded = !expanded">
      <div class="estate-header-info">
        <h4 class="estate-title">{{ $t('calculator.estate') }}</h4>
        <p class="estate-hint">{{ $t('calculator.estateOptional') }}</p>
      </div>
      <svg
        class="chevron"
        :class="{ expanded }"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>

    <div v-if="expanded" class="estate-fields">
      <div class="field">
        <label class="field-label">{{ $t('calculator.estateValue') }}</label>
        <div class="currency-input">
          <span class="currency-symbol">{{ $t('calculator.currency') }}</span>
          <input
            type="number"
            :value="estateValue"
            @input="$emit('update:estateValue', $event.target.value ? Number($event.target.value) : null)"
            placeholder="0"
            min="0"
            class="field-input"
          />
        </div>
      </div>

      <!-- Deductions (only shown if estate value is set) -->
      <template v-if="estateValue">
        <div class="deductions-label">{{ $t('calculator.deductions') }}</div>

        <div class="field">
          <label class="field-label">{{ $t('calculator.funeralExpenses') }}</label>
          <div class="currency-input">
            <span class="currency-symbol">{{ $t('calculator.currency') }}</span>
            <input
              type="number"
              :value="funeralExpenses"
              @input="$emit('update:funeralExpenses', $event.target.value ? Number($event.target.value) : null)"
              placeholder="0"
              min="0"
              class="field-input"
            />
          </div>
        </div>

        <div class="field">
          <label class="field-label">{{ $t('calculator.debts') }}</label>
          <div class="currency-input">
            <span class="currency-symbol">{{ $t('calculator.currency') }}</span>
            <input
              type="number"
              :value="debts"
              @input="$emit('update:debts', $event.target.value ? Number($event.target.value) : null)"
              placeholder="0"
              min="0"
              class="field-input"
            />
          </div>
        </div>

        <div class="field">
          <label class="field-label">{{ $t('calculator.unpaidMahr') }}</label>
          <div class="currency-input">
            <span class="currency-symbol">{{ $t('calculator.currency') }}</span>
            <input
              type="number"
              :value="unpaidMahr"
              @input="$emit('update:unpaidMahr', $event.target.value ? Number($event.target.value) : null)"
              placeholder="0"
              min="0"
              class="field-input"
            />
          </div>
        </div>

        <div class="field">
          <label class="field-label">
            {{ $t('calculator.wasiyyahAmount') }}
            <span class="field-hint">{{ $t('calculator.wasiyyahNote') }}</span>
          </label>
          <div class="currency-input">
            <span class="currency-symbol">{{ $t('calculator.currency') }}</span>
            <input
              type="number"
              :value="wasiyyahAmount"
              @input="$emit('update:wasiyyahAmount', $event.target.value ? Number($event.target.value) : null)"
              placeholder="0"
              min="0"
              class="field-input"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const props = defineProps({
  estateValue: { type: Number, default: null },
  funeralExpenses: { type: Number, default: null },
  debts: { type: Number, default: null },
  unpaidMahr: { type: Number, default: null },
  wasiyyahAmount: { type: Number, default: null },
})

defineEmits([
  'update:estateValue',
  'update:funeralExpenses',
  'update:debts',
  'update:unpaidMahr',
  'update:wasiyyahAmount',
])

const expanded = ref(!!props.estateValue)
</script>

<style scoped>
.estate-form {
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.estate-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4);
  cursor: pointer;
  transition: background 0.15s ease;
}

.estate-header:hover {
  background: var(--color-gray-50);
}

.estate-header-info {
  flex: 1;
}

.estate-title {
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

.estate-hint {
  margin: var(--space-1) 0 0 0;
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.chevron {
  color: var(--color-gray-400);
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.chevron.expanded {
  transform: rotate(180deg);
}

.estate-fields {
  padding: 0 var(--space-4) var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}

.deductions-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding-top: var(--space-2);
  border-top: 1px solid var(--color-gray-100);
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.field-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
}

.field-hint {
  font-weight: normal;
  color: var(--color-gray-400);
  font-size: var(--font-size-xs);
  margin-inline-start: var(--space-1);
}

.currency-input {
  display: flex;
  align-items: center;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  overflow: hidden;
  direction: ltr; /* keep ₹ on the left — currency symbol position is not directional */
}

.currency-input:focus-within {
  border-color: var(--color-blue);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

.currency-symbol {
  padding: var(--space-2) var(--space-3);
  background: var(--color-gray-50);
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
  border-inline-end: 1px solid var(--color-gray-200);
}

.field-input {
  flex: 1;
  padding: var(--space-2) var(--space-3);
  border: none;
  outline: none;
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  background: transparent;
}

.field-input::placeholder {
  color: var(--color-gray-300);
}

/* Remove number input spinners */
.field-input::-webkit-outer-spin-button,
.field-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.field-input[type="number"] {
  -moz-appearance: textfield;
}
</style>
